import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { JOB_ROLES } from "@/lib/api";
import { JOB_FIELDS } from "@/lib/schemas";
import api from "@/lib/api";
import DynamicField from "@/components/DynamicField";
import PhoneInput from "@/components/PhoneInput";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";

export default function RegisterStaff() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") || "";
  const [step, setStep] = useState(initialRole ? 2 : 1);
  const [role, setRole] = useState(initialRole);
  const [acct, setAcct] = useState({ name: "", email: "", password: "", phone: "" });
  const [appDetails, setAppDetails] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setDetail = (k, v) => setAppDetails((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!role) {
      toast.error("Please pick a role.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register-staff", {
        ...acct,
        role,
        application_details: appDetails,
      });
      localStorage.setItem("tw_token", data.access_token);
      await refresh();
      toast.success("Account created — application submitted.");
      nav("/staff");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16" data-testid="register-staff-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/register" className="inline-flex items-center gap-1 text-sm text-black/60 hover:text-black" data-testid="staff-back-to-choice">
          <ArrowLeft size={16} /> Back
        </Link>
        <span className="font-mono text-xs text-black/40">/ STAFF REGISTRATION</span>
      </div>

      <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter leading-[0.9] max-w-3xl">
        Join the team.<br />Step {step} of 2.
      </h1>

      {step === 1 && (
        <div className="mt-12" data-testid="staff-step-1">
          <p className="label-eyebrow">Pick a role</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            {JOB_ROLES.map((r, idx) => (
              <button
                key={r.key}
                type="button"
                onClick={() => { setRole(r.key); setAppDetails({}); setStep(2); }}
                data-testid={`pick-role-${r.key}`}
                className="text-left border border-black p-5 hover:bg-black hover:text-white transition-colors"
              >
                <p className="font-mono text-[10px] opacity-60">{String(idx + 1).padStart(2, "0")} / {r.section.toUpperCase()}</p>
                <p className="font-display text-xl font-black mt-2">{r.name}</p>
              </button>
            ))}
          </div>
          <p className="text-sm text-black/60 mt-10">
            Already have a staff account? <Link to="/login" className="underline">Sign in</Link>
          </p>
        </div>
      )}

      {step === 2 && role && (
        <form onSubmit={submit} className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10" data-testid="staff-step-2">
          <div className="md:col-span-7 space-y-6">
            <div>
              <p className="label-eyebrow">Account credentials</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-3">
                <Field label="Full name" value={acct.name} onChange={(v) => setAcct({ ...acct, name: v })} testid="staff-acct-name" />
                <div>
                  <label className="label-eyebrow block mb-2">Phone</label>
                  <PhoneInput value={acct.phone} onChange={(v) => setAcct({ ...acct, phone: v })} required={true} testid="staff-acct-phone" />
                </div>
                <Field label="Email" type="email" value={acct.email} onChange={(v) => setAcct({ ...acct, email: v })} testid="staff-acct-email" />
                <Field label="Password" type="password" value={acct.password} onChange={(v) => setAcct({ ...acct, password: v })} testid="staff-acct-password" />
              </div>
            </div>
            <div>
              <p className="label-eyebrow">Application details</p>
              <p className="text-sm text-black/60 mt-1 mb-4">
                Applying for <b>{JOB_ROLES.find((r) => r.key === role)?.name}</b>.{" "}
                <button type="button" className="underline" onClick={() => { setRole(""); setStep(1); }} data-testid="staff-change-role">Change role</button>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {(JOB_FIELDS[role] || []).map((f) => (
                  <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                    <DynamicField field={f} value={appDetails[f.name]} onChange={setDetail} context={`staff-register-${role}`} />
                  </div>
                ))}
              </div>
            </div>
            <button disabled={submitting} className="btn-primary" type="submit" data-testid="staff-submit">
              {submitting ? "Submitting…" : "Create account & submit application"} <ArrowRight size={16} />
            </button>
          </div>
          <aside className="md:col-span-5">
            <div className="border border-black p-6 sticky top-24 bg-black text-white">
              <p className="label-eyebrow text-white/60">What happens next</p>
              <ol className="text-sm space-y-3 mt-4 list-decimal pl-4">
                <li>Your account is created.</li>
                <li>Your application enters review (status: pending).</li>
                <li>You can track approval in your staff dashboard.</li>
                <li>We email you when status changes.</li>
              </ol>
            </div>
          </aside>
        </form>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        type={type}
        required
        className="input-sharp"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
